// `PanelTitleRow` — the Progress panels' title layout: left-aligned title, the
// `?` pill on the right, and the help body below when it is open. The pill and
// the body themselves are the shared <InfoPill>/<InfoPillBody> primitives (the
// program composer uses the same pair on a table column header); this file owns
// only the title ROW arrangement. [[feedback_quick_tip_pill_layout]]
import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { InfoPill, InfoPillBody } from "../primitives/InfoPill";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { scaled } from "../pictureScale";
import type { UiTheme } from "../theme";
import {
  WidgetCollapseButton,
  WidgetCollapseContext,
} from "./WidgetCollapseButton";

// PG-64: true under a tree drawn as a picture (the report's shared image).
// Every panel title below it drops its help pill and its gesture hint, with
// nothing to pass: ProgressReportContent provides it once for the image,
// the model is WidgetCollapseContext.
export const ProgressPictureContext = createContext(false);

// Convenience layout: pairs a left-aligned title with the pill on the
// right and the (optional) help body below. Drop-in for a panel's title.
export function PanelTitleRow({
  theme,
  title,
  body,
  period,
  right,
  caption,
}: {
  theme: UiTheme;
  title: string;
  body: string;
  period?: string;
  right?: ReactNode;
  /** PG-34: one always-visible line under the title, e.g. the chart cursor
   *  hint. Not the InfoPill body, which stays a disclosure: this is a
   *  standing caption, so pass it only where it is always true. Rendered
   *  `hiddenFromScreenReader`: today's only caller describes a visual-only
   *  gesture, so a screen reader user gets nothing to act on from it. */
  caption?: string;
}) {
  const [open, setOpen] = useState(false);
  const styles = titleStyles(theme);
  const interactive = !useContext(ProgressPictureContext);
  return (
    <View>
      <WidgetTitleRow
        theme={theme}
        title={title}
        right={
          <>
            {right}
            {period ? <Text style={styles.period}>{period}</Text> : null}
            {interactive ? (
              <InfoPill
                theme={theme}
                open={open}
                onToggle={() => setOpen((v) => !v)}
              />
            ) : null}
          </>
        }
      />
      {caption && interactive ? (
        <Text style={styles.caption} {...hiddenFromScreenReader()}>
          {caption}
        </Text>
      ) : null}
      {open ? <InfoPillBody theme={theme} text={body} /> : null}
    </View>
  );
}

/** The bare title row of a Progress widget: the title on the left, the right
 *  cluster, and at its end the ▲ of an open collapsible widget (PG-57).
 *  PanelTitleRow composes it; the cards without a help pill (loading and
 *  error, rhythm, volume by tag, coverage) use it alone. A string title takes
 *  the eyebrow voice; a node keeps its own. */
export function WidgetTitleRow({
  theme,
  title,
  right,
}: {
  theme: UiTheme;
  title: ReactNode;
  right?: ReactNode;
}) {
  const styles = titleStyles(theme);
  const collapsible = useContext(WidgetCollapseContext) !== null;
  return (
    <View style={styles.titleRow}>
      {typeof title === "string" ? (
        <Text style={styles.title} accessibilityRole="header">
          {title}
        </Text>
      ) : (
        <View style={styles.titleSlot}>{title}</View>
      )}
      {right || collapsible ? (
        <View style={styles.rightCluster}>
          {right}
          <WidgetCollapseButton theme={theme} />
        </View>
      ) : null}
    </View>
  );
}

/** The panel title's eyebrow voice. A collapsed Progress widget names itself
 *  with it too, so closed and open read as the same widget (PG-57). */
export function panelTitleTextStyle(theme: UiTheme) {
  const { palette: p, typography: ty } = theme;
  return {
    color: p.ink3,
    fontSize: ty.sizes.xs,
    fontWeight: "700",
    letterSpacing: scaled(theme, 0.6),
    textTransform: "uppercase",
  } as const;
}

function titleStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    titleRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s2,
    },
    title: { ...panelTitleTextStyle(theme), flex: 1, minWidth: 0 },
    titleSlot: { flex: 1, minWidth: 0 },
    rightCluster: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
    },
    period: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    // Shared recipe: see primitives/helpTextStyle. Local diff is marginTop
    // only.
    caption: {
      ...helpTextStyle(theme),
      marginTop: s.s1,
    },
  });
}
